import React from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { Router } from 'react-router-dom'

/**
 * A reference to react stateful component or null if stateless
 * @name ComponentReference
 * @type RefObject<React.Component> | null
 */

/**
 * React class or functional component
 * @name ReactComponent
 * @type React.Component | function (*)
 */

/**
 * Environment parameters for creating components.
 * @name ComponentEnvironment
 * @class
 * @property store - a redux-compatible store
 * @property history - a history
 */

/**
 * Internal structure to hold information regarding container node and
 * extracted props for component
 * @name ContainerInstance
 * @class
 * @property {Node} container - container element
 * @property {Object} props - initial props
 */

/**
 * Structure for rendered component
 * @name ComponentInstance
 * @class
 * @extends ContainerInstance
 * @property {ComponentReference} component - a reference to the component (or null for stateless components)
 * @property {function (*): boolean} unmount - helper to unmount the component. Uses {@link ReactDOM.unmountComponentAtNode}
 */

/**
 * Use ReactDOM to render the element inside the container and resolve when
 * rendering is done
 * @param {ReactComponent} element - react component to render
 * @param {Node} container - dom node to render inside
 * @return {Promise<ComponentReference>} a reference to the component (or returns null for stateless components)
 */
const asyncRender = (element, container) =>
  // manually promisify instead of util.promisify because this will run in browser
  // eslint-disable-next-line react/no-render-return-value
  new Promise(resolve => ReactDOM.render(element, container, resolve))

/**
 * Retrieve all elements for a given component
 * @param {string} componentName component name to search elements for in the current document
 * @return {ContainerInstance[]}
 */
const findContainersForComponent = (componentName) => [
  // gather with tag name
  ...Array.from(document.getElementsByTagName(componentName))
    .map(container => {
      const props = Object.assign({},
        ...Array.from(container.attributes, ({ name, value }) => ({ [name]: value })),
        { children: container.innerHTML }
      )
      return { container, props }
    }),
  // gather with prop name
  ...Array.from(document.querySelectorAll(`[${componentName}]`))
    .map(container => {
      const { value } = container.attributes[componentName]
      container.attributes[componentName].value = '/* parsed */'
      return {
        container,
        props: value ? JSON.parse(value) : {}
      }
    })
]

/**
 * Create a component rendering function wrapped with the store and router
 * @param {ReactComponent} Component - component to create
 * @param {ComponentEnvironment} env - component environment
 * @return {function(ContainerInstance): Promise<ComponentInstance>} a render function to render the component in a container with props
 */
const createComponentRender = (Component, { store, history }) =>
  /**
   * Render a component in a container with props
   * @param {ContainerInstance} containerInstance - container instance
   * @return {Promise<ComponentInstance>} a reference to the rendered component, unmount function and the container and props
   */
  async ({ container, props }) => {
    const component = await asyncRender((
      <Provider store={store}>
        <Router history={history}>
          <Component {...props} />
        </Router>
      </Provider>
    ), container)
    const unmount = () => ReactDOM.unmountComponentAtNode(container)
    return {
      component,
      container,
      props,
      unmount
    }
  }

/**
 * Create components renderers
 * @param {Object} components - a map of internal component names to React.Components
 * @param {ComponentEnvironment} env - Required params for creation of component
 * @return {function(string): Promise<ComponentInstance[]>} a component renderer
 */
const createComponentsRenderers = (components, env) =>
  /**
   * Render component into all available containers based on the component name
   * @param {string} internalComponentName internal component name
   * @return {Promise<ComponentInstance[]>}
   */
  async (internalComponentName) => {
    const componentName = `yf-${internalComponentName}`
    const containers = findContainersForComponent(componentName)

    const Component = components[internalComponentName]
    const componentRender = createComponentRender(Component, env)
    return Promise.all(containers.map(componentRender))
  }

/**
 * Combine two promises resulting in lists
 * @param {Promise<*[]>} a first promise to list
 * @param {Promise<*[]>} b second promise to list
 * @return {Promise<*[]>} Concatenated lists from a and b
 */
const reduceComponentCreations = async (a, b) => [...await a, ...await b]

/**
 * Render a set of components to dom elements based on their names
 * @param {Object} components - a map of internal component names to React.Components
 * @param {ComponentEnvironment} env - Required params for creation of component
 * @return {Promise<ComponentInstance[]>}
 */
export default async ({ components, ...env }) => Object
  .keys(components)
  .map(createComponentsRenderers(components, env))
  .reduce(reduceComponentCreations)
